import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useQuizStore } from "../stores/quizStore";
import { isQuestionSlide } from "../types";

/** Where the welcome slide's back arrow leads (the quiz page with no hash). */
const WELCOME_BACK_HREF = "/carbon-footprint-quiz#";

export function useQuizUi() {
    const quiz = useQuizStore();
    const route = useRoute();
    const router = useRouter();

    const isFeedbackRoute = computed(() => route.name === "feedback");
    const isIntroRoute = computed(() => route.name === "questionIntro");
    const isScoreRoute = computed(() => route.name === "score");
    const feedbackId = computed(() => String(route.params.id || ""));

    const isQuestionContext = computed(() => {
        if (isFeedbackRoute.value) return true;
        if (isIntroRoute.value) return true;
        const s = quiz.currentSlide;
        return !!(s && isQuestionSlide(s));
    });

    const currentQuestionId = computed(() => {
        if (isFeedbackRoute.value) return feedbackId.value;

        const s = quiz.currentSlide;
        if (!s) return null;
        if (!isQuestionSlide(s)) return null;

        return s.questionId;
    });

    const currentQuestionNumber = computed(() => {
        if (!currentQuestionId.value) return 0;
        return quiz.getQuestionIndex(currentQuestionId.value) + 1;
    });

    const transitionName = computed(() => {
        if (isFeedbackRoute.value) return "feedback-fade";

        const s = quiz.currentSlide;
        if (!s) return "quiz-fade";

        switch (s.kind) {
            case "question":
                return "slide-fade";
            default:
                return "quiz-fade";
        }
    });

    const canBack = computed(() => {
        const s = quiz.currentSlide;
        if (!s) return false;
        if (isFeedbackRoute.value) return true;
        if (isIntroRoute.value) return false;
        if (s.route === "/welcome") return false;
        // if (s.route === "/score") return false;
        return true;
    });

    // On the welcome slide the back arrow is a plain link out of the quiz.
    const backHref = computed(() => {
        const s = quiz.currentSlide;
        if (!s) return null;
        if (isFeedbackRoute.value || isIntroRoute.value) return null;
        return s.route === "/welcome" ? WELCOME_BACK_HREF : null;
    });

    const canForward = computed(() => {
        const s = quiz.currentSlide;
        if (!s) return false;

        if (s.route === "/welcome") return false;
        if (s.route === "/score") return false;
        if (s.route === "/start") return false;

        if (route.name === "feedback") return false;
        if (route.name === "questionIntro") return false;

        if (s.kind === "question") {
            return quiz.shouldShowRevisitControls(s.questionId);
        }

        return quiz.currentIndex < quiz.slides.length - 1;
    });

    function goBack() {
        router.back();
    }

    function goForward() {
        if (route.name === "feedback") {
            const id = String(route.params.id || "");
            const idx = quiz.getQuestionIndex(id);
            const nextQ = quiz.questions[idx + 1];

            if (!nextQ) return router.push("/score");

            return router.push(`/${nextQ.questionId}-intro`);
        }

        const s = quiz.currentSlide;
        if (!s) return;

        if (s.kind === "question") {
            router.push({ name: "feedback", params: { id: s.questionId } });
            return;
        }

        const next = quiz.slides[quiz.currentIndex + 1];
        if (next) router.push(next.route);
    }

    function goForwardFromQuestion(questionId: string) {
        router.push({ name: "feedback", params: { id: questionId } });
    }

    return {
        quiz,
        route,
        router,

        isFeedbackRoute,
        feedbackId,
        isScoreRoute,

        isQuestionContext,
        currentQuestionId,
        currentQuestionNumber,

        transitionName,

        canBack,
        canForward,
        backHref,

        goBack,
        goForward,
        goForwardFromQuestion,
    };
}
